import React, { useEffect, useRef, useState } from 'react';
import StatBox from '../ui/StatBox';
import { GameIntro, GameOver, GameState, useSaveGameResult } from './GameShell';
import { playCorrectSound, playWrongSound, playSuccessSound, playGameOverSound } from '../../utils/audio';
import { User } from '../../types';
import { ScorePops, useScorePops } from '../ui/ScorePops';

const GAME_SECONDS = 60;
const CORRECT_PER_LEVEL = 5;

interface Problem { text: string; answer: number }

const rand = (min: number, max: number) => min + Math.floor(Math.random() * (max - min + 1));

const makeProblem = (level: number): Problem => {
  const kind = level >= 5 ? rand(2, 4) : level;
  switch (kind) {
    case 1: { const a = rand(1, 9), b = rand(1, 9); return { text: `${a} + ${b}`, answer: a + b }; }
    case 2: {
      if (Math.random() < 0.5) { const a = rand(2, 12), b = rand(1, 8); return { text: `${a} + ${b}`, answer: a + b }; }
      const a = rand(6, 20), b = rand(1, a); return { text: `${a} − ${b}`, answer: a - b };
    }
    case 3: {
      if (Math.random() < 0.5) { const a = rand(11, 89), b = rand(2, 9 + (level >= 5 ? 40 : 0)); return { text: `${a} + ${b}`, answer: a + b }; }
      const a = rand(20, 99), b = rand(2, level >= 5 ? a : 9); return { text: `${a} − ${b}`, answer: a - b };
    }
    default: { const a = rand(2, level >= 5 ? 12 : 10), b = rand(2, 10); return { text: `${a} × ${b}`, answer: a * b }; }
  }
};

const NumberNinjaGame: React.FC<{ user?: User }> = ({ user }) => {
  const [gameState, setGameState] = useState<GameState>('ready');
  const [problem, setProblem] = useState<Problem>(() => makeProblem(1));
  const [typed, setTyped] = useState('');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [solved, setSolved] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_SECONDS);
  const [slice, setSlice] = useState<{ id: number; text: string } | null>(null);
  const [shake, setShake] = useState(false);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const endsAt = useRef(0);
  const { pops, pop } = useScorePops();
  // Read by the key handler so back-to-back keystrokes never see stale state
  // (two keys can arrive before React re-renders with the first one).
  const live = useRef({ typed: '', problem, solved: 0, streak: 0, level: 1 });
  const updateTyped = (v: string) => { live.current.typed = v; setTyped(v); };

  const startGame = () => {
    const first = makeProblem(1);
    live.current = { typed: '', problem: first, solved: 0, streak: 0, level: 1 };
    setProblem(first);
    setTyped(''); setScore(0); setLevel(1); setSolved(0); setStreak(0); setBestStreak(0); setWrong(0);
    setTimeLeft(GAME_SECONDS); setSlice(null);
    endsAt.current = Date.now() + GAME_SECONDS * 1000;
    setGameState('playing');
  };

  useSaveGameResult(user, 'number-ninja', gameState, score);

  useEffect(() => {
    if (gameState !== 'playing') return;
    const t = setInterval(() => {
      const left = Math.max(0, Math.ceil((endsAt.current - Date.now()) / 1000));
      setTimeLeft(left);
      if (left === 0) { playGameOverSound(); setGameState('over'); }
    }, 250);
    return () => clearInterval(t);
  }, [gameState]);

  const check = (answer: string) => {
    const { problem, solved, streak, level } = live.current;
    if (Number(answer) === problem.answer) {
      const nextSolved = solved + 1;
      const nextLevel = 1 + Math.floor(nextSolved / CORRECT_PER_LEVEL);
      const nextStreak = streak + 1;
      setScore(sc => sc + 10 * level + Math.min(nextStreak, 10) * 2);
      pop(`+${10 * level + Math.min(nextStreak, 10) * 2}`, 70, 12, '#fde047');
      setSolved(nextSolved);
      setStreak(nextStreak);
      setBestStreak(b => Math.max(b, nextStreak));
      setSlice({ id: Date.now(), text: `${problem.text} = ${problem.answer}` });
      if (nextLevel > level) { setLevel(nextLevel); playSuccessSound(); } else playCorrectSound();
      let next = makeProblem(nextLevel);
      if (next.text === problem.text) next = makeProblem(nextLevel);
      live.current = { ...live.current, problem: next, solved: nextSolved, streak: nextStreak, level: Math.max(level, nextLevel) };
      setProblem(next);
    } else {
      playWrongSound();
      setWrong(w => w + 1);
      live.current.streak = 0;
      setStreak(0);
      setShake(true);
      setTimeout(() => setShake(false), 400);
    }
    updateTyped('');
  };

  useEffect(() => {
    if (gameState !== 'playing') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        setLastKey(e.key);
        setTimeout(() => setLastKey(k => (k === e.key ? null : k)), 120);
        const next = live.current.typed + e.key;
        if (next.length >= String(live.current.problem.answer).length) check(next);
        else updateTyped(next);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        updateTyped(live.current.typed.slice(0, -1));
      } else if (e.key === 'Enter' && live.current.typed) {
        e.preventDefault();
        check(live.current.typed);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, typed, problem, solved, streak, level]);

  if (gameState === 'ready') {
    return (
      <GameIntro icon="🥷" title="Number Ninja" skill="the number row -- reach up for digits without looking." accent="text-emerald-600" button="bg-gradient-to-r from-emerald-500 to-teal-500" onStart={startGame}>
        Slice through math problems by typing the answers with the number keys on the top row. The answer is checked as soon as you type enough digits. Every 5 correct answers the problems get tougher -- addition, subtraction, then multiplication. You have 60 seconds!
      </GameIntro>
    );
  }
  if (gameState === 'over') {
    const total = solved + wrong;
    return (
      <GameOver
        title="Time's Up, Ninja!"
        stars={solved >= 25 ? 3 : solved >= 12 ? 2 : solved > 0 ? 1 : 0}
        message={solved >= 25 ? 'Lightning-fast fingers!' : solved >= 12 ? 'Sharp slicing!' : 'Keep training, young ninja!'}
        stats={[{ label: 'Score', value: score }, { label: 'Solved', value: solved }, { label: 'Best Streak', value: bestStreak }, { label: 'Accuracy', value: `${total ? Math.round((solved / total) * 100) : 100}%` }]}
        button="bg-gradient-to-r from-emerald-500 to-teal-500"
        onRestart={startGame}
      />
    );
  }

  const digits = String(problem.answer).length;
  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-around items-center mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
        <StatBox label="Time" value={timeLeft} />
        <StatBox label="Score" value={score} />
        <StatBox label="Belt" value={level} />
        <StatBox label="Streak" value={streak} />
      </div>
      <div className="relative overflow-hidden rounded-2xl border-4 border-white shadow-inner bg-gradient-to-b from-emerald-600 via-teal-700 to-emerald-900 py-10 flex flex-col items-center select-none" style={{ minHeight: 360 }}>
        <div className="absolute left-6 bottom-6 text-6xl" aria-hidden="true">🥷</div>
        {slice && (
          <div key={slice.id} className="absolute top-10 left-1/2 pointer-events-none" aria-hidden="true">
            <div className="nn-half nn-left px-8 py-5 rounded-xl bg-amber-700 text-amber-50 font-mono text-4xl font-bold">{slice.text}</div>
            <div className="nn-half nn-right px-8 py-5 rounded-xl bg-amber-700 text-amber-50 font-mono text-4xl font-bold">{slice.text}</div>
          </div>
        )}
        <div className={`relative px-10 py-6 rounded-xl bg-gradient-to-b from-amber-600 to-amber-800 border-4 border-amber-900 shadow-2xl ${shake ? 'shake' : ''}`}>
          <span className="font-mono text-5xl md:text-6xl font-bold text-amber-50 drop-shadow">{problem.text} =</span>
        </div>
        <div className="flex gap-2 mt-8">
          {Array.from({ length: digits }, (_, i) => (
            <div key={i} className={`w-14 h-16 rounded-xl border-2 flex items-center justify-center font-mono text-4xl font-bold ${i < typed.length ? 'bg-white border-emerald-300 text-emerald-800' : i === typed.length ? 'bg-white/90 border-yellow-300 shadow-[0_0_12px_rgba(253,224,71,0.6)]' : 'bg-white/20 border-white/30'}`}>
              {typed[i] ?? ''}
            </div>
          ))}
        </div>
        <div className="flex gap-1 mt-8" aria-hidden="true">
          {[...'1234567890'].map(d => (
            <span key={d} className={`w-8 h-8 rounded-md flex items-center justify-center font-mono text-sm font-bold border transition-transform ${lastKey === d ? 'scale-90 bg-yellow-300 border-yellow-400 text-emerald-900' : 'bg-white/10 border-white/20 text-white/80'}`}>{d}</span>
          ))}
        </div>
        <ScorePops pops={pops} />
        <style>{`
          .nn-half { position: absolute; top: 0; left: 0; transform: translateX(-50%); white-space: nowrap; }
          .nn-left { clip-path: polygon(0 0, 55% 0, 45% 100%, 0 100%); animation: nn-left 0.6s ease-out forwards; }
          .nn-right { clip-path: polygon(55% 0, 100% 0, 100% 100%, 45% 100%); animation: nn-right 0.6s ease-out forwards; }
          @keyframes nn-left { to { transform: translate(-90%, 120px) rotate(-18deg); opacity: 0; } }
          @keyframes nn-right { to { transform: translate(-10%, 120px) rotate(18deg); opacity: 0; } }
        `}</style>
      </div>
      <p className="text-center text-sm text-gray-500 mt-3">Use the number keys along the top of your keyboard.</p>
    </div>
  );
};

export default NumberNinjaGame;
